import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Sun, 
  Wind, 
  Zap, 
  Users, 
  ArrowRight, 
  Leaf, 
  TrendingDown, 
  Shield,
  Home,
  Building2,
  ChevronRight,
  MapPin,
  ChevronLeft,
  BookOpen,
  Play,
  Menu,
  X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [currentFlyer, setCurrentFlyer] = useState(0);
  const [slideDirection, setSlideDirection] = useState('next');
  const [isAnimating, setIsAnimating] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const flyerImages = [
    '/media/flyer-page-1.jpg',
    '/media/flyer-page-2.jpg',
    '/media/flyer-page-3.jpg',
    '/media/flyer-page-4.jpg',
  ];
  const flyerCaptions = [
    "Green Teva Énergies - Coopératives solaires nouvelle génération",
    "Les étapes d'une boucle Green Teva Énergies",
    "Les avantages pour votre communauté",
    "Rejoignez le mouvement Green Teva"
  ];

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/register');
    }
  };

  const goToFlyer = (idx) => {
    if (isAnimating || idx === currentFlyer) return;
    setIsAnimating(true);
    setSlideDirection(idx > currentFlyer ? 'next' : 'prev');
    setCurrentFlyer(idx);
    setTimeout(() => setIsAnimating(false), 600);
  };

  const prevFlyer = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setSlideDirection('prev');
    setCurrentFlyer((prev) => (prev === 0 ? flyerImages.length - 1 : prev - 1));
    setTimeout(() => setIsAnimating(false), 600);
  };

  const nextFlyer = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setSlideDirection('next');
    setCurrentFlyer((prev) => (prev === flyerImages.length - 1 ? 0 : prev + 1));
    setTimeout(() => setIsAnimating(false), 600);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img src="/logo.png" alt="Green Teva Énergie" className="h-10 sm:h-12 rounded-xl object-contain" />
            </Link>
            
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              <a href="#how-it-works" className="nav-link text-sm lg:text-base">Comment ça marche</a>
              <a href="#presentation" className="nav-link text-sm lg:text-base">Présentation</a>
              <a href="#video" className="nav-link text-sm lg:text-base">Vidéo</a>
              <a href="#benefits" className="nav-link text-sm lg:text-base">Avantages</a>
            </div>
            
            <div className="hidden md:flex items-center gap-3">
              {isAuthenticated ? (
                <Button 
                  onClick={() => navigate('/dashboard')} 
                  className="btn-primary"
                  data-testid="dashboard-btn"
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-primary font-medium" data-testid="login-btn">
                      Connexion
                    </Button>
                  </Link>
                  <Button 
                    onClick={handleGetStarted} 
                    className="btn-primary"
                    data-testid="get-started-btn"
                  >
                    Commencer
                  </Button>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-primary/10 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6 text-primary" /> : <Menu className="w-6 h-6 text-primary" />}
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-3 pb-4 border-t border-green-100 pt-4 space-y-3" data-testid="mobile-menu">
              <a href="#how-it-works" className="block py-2 text-foreground font-medium" onClick={() => setMobileMenuOpen(false)}>Comment ça marche</a>
              <a href="#presentation" className="block py-2 text-foreground font-medium" onClick={() => setMobileMenuOpen(false)}>Présentation</a>
              <a href="#video" className="block py-2 text-foreground font-medium" onClick={() => setMobileMenuOpen(false)}>Vidéo</a>
              <a href="#benefits" className="block py-2 text-foreground font-medium" onClick={() => setMobileMenuOpen(false)}>Avantages</a>
              <div className="flex gap-3 pt-2">
                {isAuthenticated ? (
                  <Button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="btn-primary flex-1">Dashboard</Button>
                ) : (
                  <>
                    <Link to="/login" className="flex-1"><Button variant="ghost" className="w-full text-primary font-medium">Connexion</Button></Link>
                    <Button onClick={() => { handleGetStarted(); setMobileMenuOpen(false); }} className="btn-primary flex-1">Commencer</Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[80vh] sm:min-h-screen flex items-center pt-16 sm:pt-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#f8fafc]" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #16a34a 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-green-100/40 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-lime-100/30 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-20">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="space-y-6 sm:space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200/60 rounded-full text-green-700 text-xs sm:text-sm font-medium">
                <Leaf className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Coopérative Énergétique
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground leading-[1.1] tracking-tight">
                De l'énergie qui vient{' '}
                <span className="text-gradient">d'à côté</span>
              </h1>
              
              <p className="text-base sm:text-lg text-muted-foreground max-w-lg leading-relaxed">
                Vendez et achetez votre électricité renouvelable à vos voisins. 
                Nous optimisons son utilisation et les gains qu'elle génère.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2">
                <Button 
                  onClick={handleGetStarted}
                  size="lg"
                  className="btn-primary text-base px-8 sm:px-10 h-12 sm:h-14"
                  data-testid="hero-cta-btn"
                >
                  Rejoindre une communauté
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/simulator')}
                  className="btn-secondary text-base h-12 sm:h-14"
                  data-testid="simulator-cta-btn"
                >
                  Simuler mes économies
                </Button>
              </div>
              
              <div className="flex items-center gap-6 sm:gap-10 pt-4 sm:pt-6">
                <div>
                  <p className="text-2xl sm:text-3xl font-bold text-green-600 mono">-30%</p>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">sur votre facture</p>
                </div>
                <div className="w-px h-10 sm:h-12 bg-green-200/60" />
                <div>
                  <p className="text-2xl sm:text-3xl font-bold text-green-600 mono">100%</p>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">énergie locale</p>
                </div>
                <div className="w-px h-10 sm:h-12 bg-green-200/60" />
                <div>
                  <p className="text-2xl sm:text-3xl font-bold text-green-600 mono">0€</p>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">d'investissement</p>
                </div>
              </div>
            </div>
            
            <div className="relative hidden lg:block animate-fade-in-up stagger-2">
              <EnergyFlowDiagram />
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-12 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-3 sm:mb-4">
              Comment ça fonctionne ?
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              La coopérative énergétique permet de partager l'électricité produite localement entre voisins.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-8">
            {[
              { icon: Sun, title: "Production locale", description: "Les producteurs (panneaux solaires, éoliennes) injectent leur surplus sur le réseau local.", color: "from-yellow-400 to-orange-500" },
              { icon: Zap, title: "Distribution intelligente", description: "Notre algorithme répartit l'énergie selon les besoins et calcule le meilleur prix.", color: "from-green-400 to-emerald-600" },
              { icon: Home, title: "Consommation optimisée", description: "Les consommateurs profitent d'une énergie moins chère que le réseau national.", color: "from-blue-400 to-cyan-500" }
            ].map((step, index) => (
              <Card 
                key={index} 
                className="stats-card group cursor-pointer"
                onMouseEnter={() => setHoveredFeature(index)}
                onMouseLeave={() => setHoveredFeature(null)}
                data-testid={`step-card-${index}`}
              >
                <CardContent className="p-5 sm:p-8">
                  <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform`}>
                    <step.icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2 sm:mb-3">{step.title}</h3>
                  <p className="text-sm sm:text-base text-muted-foreground">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Flyer Presentation Section */}
      <section id="presentation" className="py-12 sm:py-24 bg-gradient-to-b from-green-50/60 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full text-primary text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              Notre Présentation
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-3 sm:mb-4">
              Découvrez Green Teva Énergies
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Parcourez notre plaquette pour comprendre notre vision et comment nous rendons l'énergie locale accessible à tous.
            </p>
          </div>

          {/* Flyer Carousel */}
          <div className="relative max-w-4xl mx-auto" data-testid="flyer-carousel">
            <div className="relative overflow-hidden rounded-xl sm:rounded-2xl shadow-2xl bg-white border border-green-100">
              <div className="aspect-[2/3] sm:aspect-[2/3] relative">
                {flyerImages.map((img, idx) => {
                  let transform = 'translateX(100%)';
                  let opacity = 0;
                  if (idx === currentFlyer) {
                    transform = 'translateX(0)';
                    opacity = 1;
                  } else if (
                    (slideDirection === 'next' && idx < currentFlyer) ||
                    (slideDirection === 'prev' && idx > currentFlyer)
                  ) {
                    transform = slideDirection === 'next' ? 'translateX(-100%)' : 'translateX(100%)';
                  } else {
                    transform = slideDirection === 'next' ? 'translateX(100%)' : 'translateX(-100%)';
                  }
                  return (
                    <img
                      key={idx}
                      src={img}
                      alt={`Green Teva - ${flyerCaptions[idx]}`}
                      style={{
                        transform,
                        opacity,
                        transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                      className="absolute inset-0 w-full h-full object-contain bg-white"
                      data-testid={`flyer-image-${idx}`}
                    />
                  );
                })}
              </div>

              {/* Caption overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 sm:p-6">
                <p className="text-white text-sm sm:text-lg font-semibold" key={currentFlyer} style={{ animation: 'fadeSlideUp 0.4s ease-out' }}>
                  {flyerCaptions[currentFlyer]}
                </p>
                <p className="text-white/70 text-xs sm:text-sm">Page {currentFlyer + 1} / {flyerImages.length}</p>
              </div>
            </div>

            {/* Navigation arrows */}
            <button
              onClick={prevFlyer}
              className="absolute left-1 sm:left-[-20px] top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-green-50 hover:scale-110 transition-all border border-green-100"
              data-testid="flyer-prev-btn"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            </button>
            <button
              onClick={nextFlyer}
              className="absolute right-1 sm:right-[-20px] top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-green-50 hover:scale-110 transition-all border border-green-100"
              data-testid="flyer-next-btn"
            >
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            </button>

            {/* Dots */}
            <div className="flex items-center justify-center gap-2 sm:gap-3 mt-4 sm:mt-6">
              {flyerImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToFlyer(idx)}
                  className={`h-2.5 sm:h-3 rounded-full transition-all duration-300 ${
                    idx === currentFlyer ? 'bg-primary w-6 sm:w-8' : 'bg-gray-300 hover:bg-gray-400 w-2.5 sm:w-3'
                  }`}
                  data-testid={`flyer-dot-${idx}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Video Presentation Section */}
      <section id="video" className="py-12 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full text-primary text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              Vidéo de présentation
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-3 sm:mb-4">
              Découvrez notre solution en images
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Regardez comment Green Teva Énergies rend la coopérative énergétique simple et accessible.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl border border-green-100 bg-black">
              <video
                controls
                playsInline
                preload="metadata"
                className="w-full aspect-video"
                data-testid="presentation-video"
                poster="/media/flyer-page-1.jpg"
              >
                <source src="/media/presentation.mp4" type="video/mp4" />
                Votre navigateur ne supporte pas la lecture vidéo.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section id="benefits" className="py-12 sm:py-24 gradient-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-16 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4 sm:mb-6">
                Mais qui décide du prix ?
              </h2>
              <p className="text-lg sm:text-xl text-primary font-semibold mb-3 sm:mb-4">C'est vous.</p>
              <p className="text-base sm:text-lg text-muted-foreground mb-6 sm:mb-8">
                Dans les communautés Green Teva, chacun choisit sa limite de prix. 
                Notre algorithme calcule à tout moment la meilleure répartition.
              </p>
              
              <div className="space-y-3 sm:space-y-4">
                {[
                  { icon: TrendingDown, text: "Énergie moins chère que votre fournisseur" },
                  { icon: Shield, text: "Risque zéro : votre fournisseur prend le relais" },
                  { icon: Leaf, text: "100% locale et renouvelable" },
                  { icon: Users, text: "Produite par des gens que vous connaissez" }
                ].map((benefit, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white/80 rounded-xl transition-all hover:bg-white hover:shadow-md"
                    data-testid={`benefit-${index}`}
                  >
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <benefit.icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    </div>
                    <span className="text-sm sm:text-base font-medium text-foreground">{benefit.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/29206495/pexels-photo-29206495.jpeg" 
                alt="Maison avec panneaux solaires"
                className="rounded-2xl sm:rounded-3xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-4 -left-2 sm:-bottom-6 sm:-left-6 bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl">
                <p className="text-xs sm:text-sm text-muted-foreground">Économies moyennes</p>
                <p className="text-2xl sm:text-3xl font-bold text-primary">250€/an</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Summary */}
      <section className="py-12 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">Résumons</h2>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {[
              { title: "Pas d'investissement", subtitle: "Pas de frais fixes, pas d'abonnement", icon: Shield },
              { title: "Énergie locale", subtitle: "Renouvelable et de proximité", icon: MapPin },
              { title: "Prix choisi", subtitle: "Vous fixez votre limite", icon: TrendingDown },
              { title: "Calcul optimal", subtitle: "Algorithme de répartition intelligent", icon: Zap }
            ].map((item, index) => (
              <Card key={index} className="stats-card text-center" data-testid={`summary-card-${index}`}>
                <CardContent className="p-4 sm:p-6">
                  <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <item.icon className="w-5 h-5 sm:w-7 sm:h-7 text-primary" />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-foreground mb-1">{item.title}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">{item.subtitle}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 sm:py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/33122840/pexels-photo-33122840.jpeg)' }}
        />
        <div className="absolute inset-0 bg-primary/90" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            Trouvez une communauté près de chez vous
          </h2>
          <p className="text-base sm:text-lg text-white/80 mb-6 sm:mb-8">
            Ou créez la vôtre en moins de 5 minutes, gratuitement et sans engagement.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button 
              onClick={() => navigate('/communities')}
              size="lg"
              className="bg-white text-primary hover:bg-white/90 rounded-full px-8 sm:px-10 text-base sm:text-lg font-semibold"
              data-testid="find-community-btn"
            >
              <MapPin className="mr-2 w-5 h-5" />
              Chercher
            </Button>
            <Button 
              onClick={() => navigate('/communities/create')}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 sm:px-10 text-base sm:text-lg font-semibold"
              data-testid="create-community-btn"
            >
              Créer ma communauté
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center mb-4">
                <img src="/logo.png" alt="Green Teva" className="h-10 rounded-xl object-contain" />
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                Optimiser l'énergie renouvelable locale pour un futur durable.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 text-sm text-white">Solution</h4>
              <ul className="space-y-2.5 text-slate-400 text-sm">
                <li><a href="#how-it-works" className="hover:text-green-400 transition-colors">Comment ça marche</a></li>
                <li><a href="#presentation" className="hover:text-green-400 transition-colors">Présentation</a></li>
                <li><a href="#video" className="hover:text-green-400 transition-colors">Vidéo</a></li>
                <li><Link to="/simulator" className="hover:text-green-400 transition-colors">Simulateur</Link></li>
                <li><Link to="/communities" className="hover:text-green-400 transition-colors">Communautés</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 text-sm text-white">Légal</h4>
              <ul className="space-y-2.5 text-slate-400 text-sm">
                <li><Link to="/cgu" className="hover:text-green-400 transition-colors">Conditions générales d'utilisation</Link></li>
                <li><Link to="/cgv" className="hover:text-green-400 transition-colors">Conditions générales de vente</Link></li>
                <li><Link to="/confidentialite" className="hover:text-green-400 transition-colors">Politique de confidentialité</Link></li>
                <li><Link to="/mentions-legales" className="hover:text-green-400 transition-colors">Mentions légales</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 text-sm text-white">Contact</h4>
              <p className="text-slate-400 text-sm" data-testid="contact-email">contact@greenteva-energie.com</p>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-10 sm:mt-12 pt-6 sm:pt-8 text-center text-slate-500 text-sm">
            <p>&copy; 2026 Green Teva Énergie. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Energy Flow Diagram Component
const EnergyFlowDiagram = () => {
  return (
    <div className="relative bg-white rounded-3xl p-8 shadow-lg border border-green-100/60">
      <div className="absolute -top-3 -right-3 w-20 h-20 bg-green-100/40 rounded-full blur-xl" />
      <svg viewBox="0 0 400 300" className="w-full h-auto">
        <defs>
          <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(22, 163, 74, 0.06)" strokeWidth="0.5"/>
          </pattern>
          <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22c55e"/>
            <stop offset="100%" stopColor="#16a34a"/>
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)"/>
        
        <g transform="translate(50, 80)">
          <circle cx="0" cy="0" r="35" fill="#fefce8" stroke="#eab308" strokeWidth="1.5"/>
          <Sun className="w-8 h-8" x="-16" y="-16" style={{ color: '#eab308' }}/>
          <text y="55" textAnchor="middle" className="text-xs fill-foreground font-medium">Producteur</text>
        </g>
        
        <g transform="translate(200, 80)">
          <circle cx="0" cy="0" r="35" fill="#f0fdf4" stroke="#22c55e" strokeWidth="1.5"/>
          <Zap className="w-8 h-8" x="-16" y="-16" style={{ color: '#16a34a' }}/>
          <text y="55" textAnchor="middle" className="text-xs fill-foreground font-medium">Réseau Local</text>
        </g>
        
        <g transform="translate(350, 40)">
          <circle cx="0" cy="0" r="30" fill="#f0fdf4" stroke="#16a34a" strokeWidth="1.5"/>
          <Home className="w-6 h-6" x="-12" y="-12" style={{ color: '#16a34a' }}/>
          <text y="45" textAnchor="middle" className="text-xs fill-foreground font-medium">Maison</text>
        </g>
        
        <g transform="translate(350, 120)">
          <circle cx="0" cy="0" r="30" fill="#f0fdf4" stroke="#16a34a" strokeWidth="1.5"/>
          <Building2 className="w-6 h-6" x="-12" y="-12" style={{ color: '#16a34a' }}/>
          <text y="45" textAnchor="middle" className="text-xs fill-foreground font-medium">Commerce</text>
        </g>
        
        <path d="M 85 80 L 165 80" fill="none" stroke="url(#flowGradient)" strokeWidth="2.5" strokeDasharray="8 4" className="energy-flow-line"/>
        <path d="M 235 70 L 320 45" fill="none" stroke="url(#flowGradient)" strokeWidth="2.5" strokeDasharray="8 4" className="energy-flow-line" style={{ animationDelay: '0.3s' }}/>
        <path d="M 235 90 L 320 115" fill="none" stroke="url(#flowGradient)" strokeWidth="2.5" strokeDasharray="8 4" className="energy-flow-line" style={{ animationDelay: '0.6s' }}/>
        
        <polygon points="160,75 170,80 160,85" fill="#22c55e"/>
        <polygon points="315,42 325,45 318,52" fill="#22c55e"/>
        <polygon points="318,108 325,115 315,118" fill="#22c55e"/>
        
        <g transform="translate(50, 220)">
          <rect x="0" y="0" width="300" height="55" rx="12" fill="#f0fdf4" stroke="#dcfce7" strokeWidth="1"/>
          <text x="150" y="18" textAnchor="middle" className="text-xs fill-muted-foreground">L'énergie produite localement est distribuée</text>
          <text x="150" y="33" textAnchor="middle" className="text-xs fill-muted-foreground">aux consommateurs de la communauté</text>
          <text x="150" y="48" textAnchor="middle" className="text-xs fill-primary font-semibold">au prix que VOUS décidez</text>
        </g>
      </svg>
    </div>
  );
};

export default Landing;
